import React from 'react';
import { TrashIcon } from '../icons';
import { renderIcon } from '../../SprintDetail/FlowchartView/utils/renderIcon';

/**
 * Confirmed sidebar ghosts: parentSprintId but no mission card.
 * Put back recreates the card. Trash is only for ones you do not want.
 */
const HiddenNestsSection = ({
  rows = [],
  query = '',
  unverifiedParentCount = 0,
  scanning = false,
  scanProgress = null,
  puttingBackId = null,
  onScan,
  onPutBack,
  onTrash,
}) => {
  const q = query.trim().toLowerCase();
  const visible = q
    ? rows.filter((row) => {
        const hay = `${row.sprint?.name || ''} ${row.parent?.name || ''}`.toLowerCase();
        return hay.includes(q);
      })
    : rows;

  if (visible.length === 0 && unverifiedParentCount === 0 && !scanning) {
    return null;
  }

  return (
    <div className="search-popup__hidden-nests">
      <div className="search-popup__hidden-nests-head">
        <span className="search-popup__hidden-nests-title">Hidden from sidebar</span>
        {visible.length > 0 && (
          <span className="search-popup__count">{visible.length}</span>
        )}
      </div>
      <p className="search-popup__hidden-nests-hint">
        These still say they belong to a parent, but that parent has no mission card
        for them. Put back recreates the card. Trash only if you do not need them.
      </p>

      {visible.map((row) => {
        const name = row.sprint?.name || 'Untitled';
        const parentName = row.parent?.name || 'unknown';
        const isPuttingBack = puttingBackId === row.sprint.id;
        return (
          <div key={row.sprint.id} className="search-popup__item search-popup__item--hidden-nest">
            <span className="search-popup__item-icon">
              {renderIcon(row.sprint?.icon, { size: '14px', fallback: <span>📁</span> })}
            </span>
            <div className="search-popup__item-content">
              <span className="search-popup__item-name">{name}</span>
              <span className="search-popup__item-meta">
                Belongs under {parentName}
                {row.isNameDuplicate ? ' · same name as a real nest' : ''}
              </span>
            </div>
            <div className="search-popup__item-actions search-popup__item-actions--always">
              <button
                type="button"
                className="search-popup__item-action search-popup__item-action--restore"
                title={`Put back under ${parentName}`}
                disabled={isPuttingBack || Boolean(puttingBackId)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.isNameDuplicate) {
                    if (!window.confirm(
                      `"${name}" already exists as a real nest under "${parentName}". Put this one back anyway?`
                    )) {
                      return;
                    }
                  }
                  onPutBack?.(row);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
              </button>
              <button
                type="button"
                className="search-popup__item-action search-popup__item-action--danger"
                title="Move to Trash"
                disabled={isPuttingBack || Boolean(puttingBackId)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Move "${name}" to Trash? You can restore it later.`)) {
                    onTrash?.(row.sprint.id);
                  }
                }}
              >
                <TrashIcon size={14} />
              </button>
            </div>
          </div>
        );
      })}

      {(unverifiedParentCount > 0 || scanning) && (
        <div className="search-popup__hidden-nests-scan">
          {scanning ? (
            <span className="search-popup__hidden-nests-scan-status">
              Checking parents
              {scanProgress?.total
                ? ` ${scanProgress.done} / ${scanProgress.total}`
                : '…'}
            </span>
          ) : (
            <button
              type="button"
              className="search-popup__btn"
              onClick={onScan}
            >
              Find hidden nests
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default HiddenNestsSection;
